// Painted terrain layers (public/assets/textures/terrain, CC0, docs/ASSETS.md) packed into two
// texture arrays, one layer per surface: the painted albedo (alpha = height, for height-blended
// splatting) and the matching normal map. One binding each, so the terrain shader stays well
// under the texture-slot limit however many layers it blends.
//
//   *.painted.webp ──► decode (unpremultiplied RGBA) ──► 2×2 box down to LAYER_SIZE ──► albedo[i]
//   *.normal.webp  ──► same ─────────────────────────────────────────────────────────► normal[i]
//
// A layer that fails to load is filled with a neutral value, so the map still renders.

import * as THREE from 'three/webgpu';

/** Layer index per surface (texture array depth). */
export const LAYER = { GRASS: 0, PLAINS: 1, DESERT: 2, TUNDRA: 3, SNOW: 4, ROCK: 5, CLIFF: 6, MARSH: 7, BEACH: 8 } as const;
const FILES = ['grassland', 'plains', 'desert', 'tundra', 'snow', 'rock', 'cliff', 'marsh', 'beach'] as const;

/** Texels per side of each layer: the source is 1024², halved (the close camera never needs more). */
const LAYER_SIZE = 512;
const ANISOTROPY = 8;
const NEUTRAL_ALBEDO = [128, 128, 128, 128] as const;
const NEUTRAL_NORMAL = [128, 128, 255, 255] as const;
const CHANNELS = 4;

export interface TerrainTextures {
  albedo: THREE.DataArrayTexture;
  normal: THREE.DataArrayTexture;
}

/** Load every layer under `base` (e.g. '/assets'). */
export async function loadTerrainTextures(base: string): Promise<TerrainTextures> {
  const [albedo, normal] = await Promise.all([
    loadArray(FILES.map((f) => `${base}/textures/terrain/${f}.painted.webp`), NEUTRAL_ALBEDO),
    loadArray(FILES.map((f) => `${base}/textures/terrain/${f}.normal.webp`), NEUTRAL_NORMAL),
  ]);
  albedo.colorSpace = THREE.SRGBColorSpace;
  normal.colorSpace = THREE.NoColorSpace;
  return { albedo, normal };
}

async function loadArray(urls: readonly string[], neutral: readonly number[]): Promise<THREE.DataArrayTexture> {
  const layer = LAYER_SIZE * LAYER_SIZE * CHANNELS;
  const data = new Uint8Array(layer * urls.length);
  await Promise.all(urls.map(async (url, i) => {
    try {
      const img = await decode(url);
      data.set(downsample(img, LAYER_SIZE), i * layer);
    } catch (err) {
      console.warn(`terrain textures: ${url}: ${String(err)}`);
      for (let p = 0; p < layer; p += CHANNELS) {
        data.set(neutral, i * layer + p);
      }
    }
  }));
  const tex = new THREE.DataArrayTexture(data, LAYER_SIZE, LAYER_SIZE, urls.length);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.UnsignedByteType;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = ANISOTROPY;
  tex.needsUpdate = true;
  return tex;
}

interface Pixels {
  data: Uint8Array;
  width: number;
  height: number;
}

/**
 * Decode an image to straight (unpremultiplied) RGBA. The height channel reaches 0, so any path
 * that premultiplies (2D canvas, ImageDecoder) would blacken the colour there. An ImageBitmap
 * decoded without premultiplication is uploaded to a scratch WebGL 2 texture and read back as is.
 */
async function decode(url: string): Promise<Pixels> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  try {
    return readBack(bitmap);
  } finally {
    bitmap.close();
  }
}

let scratch: WebGL2RenderingContext | null = null;

function readBack(bitmap: ImageBitmap): Pixels {
  if (!scratch) {
    scratch = new OffscreenCanvas(1, 1).getContext('webgl2');
  }
  const gl = scratch;
  if (!gl) {
    throw new Error('no WebGL 2 for texture decode');
  }
  const tex = gl.createTexture();
  const fbo = gl.createFramebuffer();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const data = new Uint8Array(bitmap.width * bitmap.height * CHANNELS);
  gl.readPixels(0, 0, bitmap.width, bitmap.height, gl.RGBA, gl.UNSIGNED_BYTE, data);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.deleteFramebuffer(fbo);
  gl.deleteTexture(tex);
  return { data, width: bitmap.width, height: bitmap.height };
}

/** Box-filter down to `size`² (the source is a power-of-two multiple of it). */
function downsample(img: Pixels, size: number): Uint8Array {
  if (img.width === size && img.height === size) {
    return img.data;
  }
  const fx = img.width / size;
  const fy = img.height / size;
  const out = new Uint8Array(size * size * CHANNELS);
  const n = fx * fy;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      for (let c = 0; c < CHANNELS; c++) {
        let sum = 0;
        for (let dy = 0; dy < fy; dy++) {
          const row = (y * fy + dy) * img.width;
          for (let dx = 0; dx < fx; dx++) {
            sum += img.data[(row + x * fx + dx) * CHANNELS + c];
          }
        }
        out[(y * size + x) * CHANNELS + c] = Math.round(sum / n);
      }
    }
  }
  return out;
}
